import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthContext, type AuthContextValue } from '../auth/AuthProvider';
import { Layout } from '../components/Layout';
import { SectionLayout } from '../components/AdminLayout';
import { AppGrid } from '../components/AppGrid';
import { AppTable } from '../components/admin/AppTable';
import { COLORS, FONTS } from '../styles/tokens';
import { useTheme } from '../hooks/useTheme';
import type { AppDefinition } from '../data/apps';
import type { KeycloakClient } from '../api/keycloak-admin';
import {
  PreviewAudit, PreviewDeploy, PreviewDeployed, PreviewOrgs,
  PreviewRequests, PreviewSettings, PreviewTak, PreviewTemplates, PreviewUsers,
} from './responsive-preview-pages';

const PREVIEW_ROLES = ['admin', 'app:cadence', 'app:cert'];

const PREVIEW_AUTH: AuthContextValue = {
  authenticated: true,
  username: 'preview',
  fullName: 'Preview User',
  email: 'preview@vertex.local',
  token: '',
  roles: PREVIEW_ROLES,
  isPending: false,
  logout: () => undefined,
  hasRole: (role: string) => PREVIEW_ROLES.includes(role),
  hasSetupPending: false,
  recheckAuthSetup: () => undefined,
};

const SAMPLE_APPS: AppDefinition[] = [
  {
    id: 'cadence', name: 'Cadence',
    description: 'Compliance and delivery Compliance Engine',
    icon: '/brand/vertex-mark-on-light.svg', launchUrl: '#', requiredRole: 'app:cadence',
    category: 'Compliance', status: 'online', team: 'Vertex',
  },
  {
    id: 'cert', name: 'Cert',
    description: 'Educational Records Training',
    icon: '/brand/vertex-mark-on-light.svg', launchUrl: '#', requiredRole: 'app:cert',
    category: 'Training', status: 'online', team: 'Vertex',
  },
  {
    id: 'echo', name: 'Echo',
    description: 'Audio and transcription platform',
    icon: '/echo-icon-on-light.png', launchUrl: '#', requiredRole: 'app:cadence',
    category: 'Platform', status: 'online', team: 'Vertex',
  },
];

const SAMPLE_CLIENTS: KeycloakClient[] = [
  {
    id: '1', clientId: 'cadence', name: 'Cadence',
    description: 'Compliance and delivery Compliance Engine',
    enabled: true, publicClient: true, redirectUris: [], webOrigins: [],
    attributes: { 'vertex.icon': '/brand/vertex-mark-on-light.svg' },
  },
  {
    id: '2', clientId: 'cert', name: 'Cert',
    description: 'Educational Records Training',
    enabled: true, publicClient: true, redirectUris: [], webOrigins: [],
    attributes: { 'vertex.hidden': 'true', 'vertex.icon': '/brand/vertex-mark-on-light.svg' },
  },
  {
    id: '3', clientId: 'cortex', name: 'Cortex',
    description: '',
    enabled: true, publicClient: true, redirectUris: [], webOrigins: [],
    attributes: {},
  },
  {
    id: '4', clientId: 'echo', name: 'Echo',
    description: 'Audio and transcription platform',
    enabled: true, publicClient: true, redirectUris: [], webOrigins: [],
    attributes: { 'vertex.icon': '/brand/vertex-mark-on-light.svg' },
  },
];

/** Local-only Hub shell so phone layouts can be reviewed without Keycloak. */
export function ResponsivePreview(): React.JSX.Element {
  return (
    <AuthContext.Provider value={PREVIEW_AUTH}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<PreviewStore />} />
            <Route path="apps" element={<SectionLayout tabs={[
              { label: 'App Management', path: '/apps', end: true },
              { label: 'App Audit', path: '/apps/audit' },
            ]} />}>
              <Route index element={<PreviewManagement />} />
              <Route path="audit" element={<PreviewAudit />} />
              <Route path="deploy" element={<PreviewDeploy />} />
              <Route path="deployed" element={<PreviewDeployed />} />
            </Route>
            <Route path="users" element={<SectionLayout tabs={[
              { label: 'User Management', path: '/users', end: true },
              { label: 'Account Requests', path: '/users/requests' },
              { label: 'Organizations', path: '/users/organizations' },
              { label: 'Templates', path: '/users/templates' },
            ]} />}>
              <Route index element={<PreviewUsers />} />
              <Route path="requests" element={<PreviewRequests />} />
              <Route path="organizations" element={<PreviewOrgs />} />
              <Route path="templates" element={<PreviewTemplates />} />
            </Route>
            <Route path="admin" element={<SectionLayout tabs={[
              { label: 'Settings', path: '/admin', end: true },
              { label: 'TAK Server', path: '/admin/tak' },
            ]} />}>
              <Route index element={<PreviewSettings />} />
              <Route path="tak" element={<PreviewTak />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

/** App Store page populated with the sample catalog. */
function PreviewStore(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 className="vx-track" style={{
          fontFamily: FONTS.heading, fontSize: '22px', letterSpacing: '0.2em',
          color: colors.text, textTransform: 'uppercase', margin: '0 0 8px',
        }}>
          App Store
        </h2>
        <p style={{ fontFamily: FONTS.body, fontSize: '14px', color: colors.textSecondary, margin: 0 }}>
          Browse available applications in the Vertex ecosystem
        </p>
      </div>
      <section>
        <h3 className="vx-track" style={{
          fontFamily: FONTS.heading, fontSize: '14px', letterSpacing: '0.15em',
          color: colors.textSecondary, textTransform: 'uppercase', margin: '0 0 16px',
        }}>
          Your Apps
        </h3>
        <AppGrid apps={SAMPLE_APPS} />
      </section>
    </div>
  );
}

/** App Management page populated with the sample client list. */
function PreviewManagement(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <div>
      <div className="vx-page-toolbar" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '20px', gap: '12px',
      }}>
        <h2 className="vx-track" style={{
          fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em',
          color: colors.text, textTransform: 'uppercase', margin: 0,
        }}>
          App Management
        </h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <PreviewButton label="Deploy App" fill />
          <PreviewButton label="Deployed Apps" />
          <PreviewButton label="Add App" />
        </div>
      </div>
      <div className="vx-table-wrap" style={{ borderRadius: '10px', border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
        <AppTable
          clients={SAMPLE_CLIENTS}
          userCounts={{ cadence: 4, cert: 2, cortex: 1, echo: 9 }}
          deployedBySlug={{}}
          isDark={isDark}
          colors={colors}
          onRowClick={() => undefined}
          onCopied={() => undefined}
        />
      </div>
    </div>
  );
}

/** Compact header action used only in the preview chrome. */
function PreviewButton({ label, fill }: { label: string; fill?: boolean }): React.JSX.Element {
  return (
    <button
      type="button"
      style={{
        height: '34px', padding: '0 16px', borderRadius: '6px',
        border: fill ? 'none' : '1px solid #3a3a3a',
        background: fill ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'transparent',
        color: fill ? '#FFF' : '#AAA',
        fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em',
        textTransform: 'uppercase', cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

/** True when this Vite session should mount the Keycloak-free preview. */
export function shouldMountResponsivePreview(): boolean {
  if (!import.meta.env.DEV) return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('responsivePreview') || window.location.pathname === '/__responsive';
}
